import axios from 'axios'
import { toast } from 'react-toastify'
import { FiKey } from 'react-icons/fi'
import { createElement } from 'react'

interface EmailApi {
  reauthenticateEmailIntegration: (params: {
    provider: string
    isShared: boolean
    email?: string
  }) => Promise<{ auth_url: string }>
}

/** Shared error handler for email send failures (missing scope, attachment errors, etc). */
export async function handleEmailError(
  err: unknown,
  emailApi: EmailApi
): Promise<void> {
  if (!axios.isAxiosError(err) || !err.response) {
    toast.error('Failed to send email. Please try again.')
    return
  }

  const errorType = err.response.data?.error_type

  if (errorType === 'missing_scope') {
    const errorData = err.response.data

    const handleReauthClick = async () => {
      if (!errorData.provider) {
        toast.error(
          'Unable to reauthenticate: provider information not available'
        )
        return
      }

      try {
        const response = await emailApi.reauthenticateEmailIntegration({
          provider: errorData.provider,
          isShared: errorData.is_shared,
          email: errorData.email || undefined,
        })

        if (response?.auth_url) {
          window.open(response.auth_url, '_blank')
        }
      } catch {
        toast.error('Error creating reauthentication URL')
      }
    }

    toast.error(
      createElement(
        'div',
        { className: 'flex items-center' },
        createElement(FiKey, { className: 'mr-2 flex-shrink-0', size: 20 }),
        createElement(
          'div',
          null,
          `${errorData.error}. `,
          createElement(
            'button',
            {
              onClick: handleReauthClick,
              className: 'font-medium underline hover:opacity-80',
            },
            'Click here to add the missing scopes'
          )
        )
      ),
      { autoClose: false }
    )
    return
  }

  if (errorType === 'attachment_too_large') {
    toast.error(
      err.response.data.error ||
        'Attachments are too large. Please reduce file size and try again.'
    )
    return
  }

  if (errorType === 'timeout') {
    toast.error(
      err.response.data.error ||
        'Email sending timed out. Large attachments may take longer to upload. Please try again or reduce file sizes.',
      { autoClose: 8000 }
    )
    return
  }

  // Handle other errors
  if (err.response.data?.error) {
    toast.error(err.response.data.error)
  } else if (err.response.data?.error_message) {
    toast.error(err.response.data.error_message)
  } else {
    toast.error('Failed to send email. Please try again.')
  }
}
